import { Component, OnInit, Input, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { GaugeObject } from 'src/app/dashboard/modules/gauge/gauge.models';
import { WallboardViewService } from '../wallboard-view.service';
import { GaugeModuleDataApiModel, WVCustom, WVMain } from '../wallboard-view.model';
import { GuageSettingsTransferObj, ModuleType } from '../../wallboards.model';

@Component({
    selector: 'cv-wallboard-view-custom-gauge',
    templateUrl: './wallboard-view-custom-gauge.component.html',
    styleUrls: ['./wallboard-view-custom-gauge.component.scss']
})
export class WallboardViewCustomGaugeComponent implements OnInit, OnDestroy {
    @Input() height;
    @Input() width;
    @Input() index;
    @Input() firstSlide;

    public wvf: WVMain;
    public wvcf: WVCustom;
    public gaugeState: GuageSettingsTransferObj = null;
    public gaugeData: GaugeModuleDataApiModel = null;
    private gaugeMetricName: string = "";

    public slideError: boolean = false;
    public titleFontSize: number | string;
    private gaugeObject: GaugeObject = null;
    private adjustedHeight;
    public localization: Map<string, string>;

    private updateTimerRef: NodeJS.Timeout = null;
    private isDestroyed: boolean = false;
    private _hasBackdrop: boolean = false;
    private _moduleKey: string = "";

    get hasBackdrop(): boolean {
        return this._hasBackdrop;
    }

    get moduleKey(): string {
        return this._moduleKey;
    }

    @ViewChild("gaugeContainer") gaugeContainer: ElementRef;

    constructor(
        private service: WallboardViewService
    ) { }

    ngOnInit(): void {
        this.wvf = this.service.WVMain;
        this.wvcf = this.service.WVCustom;

        this._hasBackdrop = this.wvcf?.Slide.HasBackdrop ?? false;
        this._moduleKey = this.wvcf?.Slide.WallboardModuleDetails[this.index].WallboardModuleKey ?? "";

        this.adjustedHeight = parseInt(this.height) > parseInt(this.width) ? Math.floor((this.width / 1.18) * 0.75) : this.height * 0.75;
        this.titleFontSize = Math.floor(this.adjustedHeight * 0.19) + "px";

        let moduleState = this.loadModuleState().then(response => {
            if (this.isDestroyed)
                return;

            this.gaugeState = response.gaugeObj;
        });

        let gaugeModuleData = this.getGaugeModuleData().then(response => {
            if (this.isDestroyed)
                return;

            this.gaugeData = response;
        });

        let metricName = this.service.getMetricName(this.wvcf.Slide.WallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.gaugeMetricName = response;
        });

        Promise.all([moduleState, gaugeModuleData, metricName]).then(responses => {
            if (!this.firstSlide) {
                let dup = false;

                for (let i = 0; i < this.wvcf.ReadyModules.length; i++) {
                    if (this.moduleKey == this.wvcf.ReadyModules[i])
                        dup = true;
                }

                if (!dup)
                    this.wvcf.ReadyModules.push(this.moduleKey);
            }

            this.runSlide();
        });
    }

    ngOnDestroy(): void {
        // End data updates
        if (this.updateTimerRef != null)
            clearInterval(this.updateTimerRef);

        if (this.gaugeObject != null) {
            this.gaugeObject.destroy();
            this.gaugeObject = null;
        }

        this.isDestroyed = true;
    }

    public async loadModuleState() {
        let latestError;

        for (let i = 0; i < 3; i++) {
            try {
                return await this.service.loadModuleState(ModuleType.Gauge, this.wvf.WallboardCreatorID, this.moduleKey);
            }
            catch (error) {
                latestError = error;
            }
        }

        this.slideError = true;

        if (!this.firstSlide)
            this.wvcf.ReadyModules.push(this.moduleKey);

        throw latestError;
    }

    public async getGaugeModuleData() {
        let latestError;

        for (let i = 0; i < 3; i++) {
            if (this.isDestroyed)
                return;

            try {
                return await this.service.getGaugeModuleData(this.wvcf.Slide.WallboardID, this.moduleKey);
            }
            catch (error) {
                latestError = error;
                await new Promise(resolve => setTimeout(resolve, WallboardViewService.ERROR_RETRY_INTERVAL));
            }
        }

        this.slideError = true;

        if (!this.firstSlide)
            this.wvcf.ReadyModules.push(this.moduleKey);

        throw latestError;
    }

    public async updateSlideData() {
        let gaugeModuleData = this.getGaugeModuleData().then(response => {
            if (this.isDestroyed)
                return;

            this.gaugeData = response;

            this.drawGauge();
        });

        let metricName = this.service.getMetricName(this.wvcf.Slide.WallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.gaugeMetricName = response;
        });

        Promise.all([gaugeModuleData, metricName]).then(responses => {
            if (this.isDestroyed)
                return;

            this.drawGauge();
        });
    }

    public runSlide() {
        if (this.isDestroyed)
            return;

        if (!this.updateTimerRef) {
            const refreshInterval = this.gaugeData.Has_Current_Metric
                ? WallboardViewService.REALTIME_DATA_REFRESH_INTERVAL
                : WallboardViewService.HISTORICAL_DATA_REFRESH_INTERVAL;

            this.updateTimerRef = setInterval(() => this.updateSlideData(), refreshInterval);
        }

        if (this.wvf.RunSlide)
            this.drawGauge();
        else {
            setTimeout(() => {
                this.runSlide();
            }, 1000);
        }
    }

    public drawGauge() {
        const VALUE_MODIFIER = 1000; // The gauge cannot handle decimal breakpoints, so we are allowing up to thousandths place and then turning into an integer

        let gaugeOptions = {
            breakpoints: [],
            colorSegments: [],
            direction: this.gaugeData.Metric.DefaultDirection,
            hover: false,
            hoverInvert: true,
            minValue: 0,
            maxValue: 0,
            title: this.gaugeMetricName,
            unit: '',
            value: this.gaugeData.Metric_Data.MetricValue * VALUE_MODIFIER,
            valueFormat: (val) => {
                val = val / VALUE_MODIFIER;
                if (val >= gaugeOptions.maxValue / VALUE_MODIFIER)
                    return this.service.formatMetricValue(this.gaugeData.Metric_Data.MetricValue, this.gaugeData.Metric.NumericType, this.gaugeData.Metric.TimeType);
                else
                    return this.service.formatMetricValue(val, this.gaugeData.Metric.NumericType, this.gaugeData.Metric.TimeType);
            }
        };

        if (this.gaugeState && this.gaugeState.isOverride) {
            // Build breakpoints from lower/alert/value
            let objRed = '#df1f26';
            let objYellow = '#f0ce14';
            let objGreen = '#97c93c';

            let objectiveValues = [
                this.gaugeState.LowerBound * VALUE_MODIFIER,
                this.gaugeState.Alert * VALUE_MODIFIER,
                this.gaugeState.Value * VALUE_MODIFIER
            ];

            if (gaugeOptions.direction === 0) { // Downward
                objectiveValues.sort((a, b) => { return a - b; });
                gaugeOptions.colorSegments = [objGreen, objYellow, objRed];
                gaugeOptions.minValue = 0;
                gaugeOptions.maxValue = objectiveValues.pop();

                if (objectiveValues[0] <= gaugeOptions.minValue) {
                    objectiveValues.splice(0, 1);
                    objectiveValues.push(gaugeOptions.maxValue);
                    gaugeOptions.maxValue = gaugeOptions.maxValue * 1.5;
                }

                gaugeOptions.breakpoints = objectiveValues;
            }
            else {
                gaugeOptions.colorSegments = [objRed, objYellow, objGreen];
                gaugeOptions.minValue = objectiveValues.splice(0, 1)[0];
                // Create length to the last breakpoint by adding the length of the first breakpoint
                gaugeOptions.breakpoints = objectiveValues;
                let lastValue = gaugeOptions.breakpoints[gaugeOptions.breakpoints.length - 1];
                let range = lastValue - gaugeOptions.minValue;
                let average = range / gaugeOptions.breakpoints.length;
                gaugeOptions.maxValue = lastValue + average;
            }
        }
        else if (this.gaugeData.Metric_Data.objectiveData) {
            // Transform objective details array into an array of the breakpoint values
            gaugeOptions.breakpoints = this.gaugeData.Metric_Data.objectiveData.poDetails.map(function (pod) { return pod.Value * VALUE_MODIFIER; });
            gaugeOptions.breakpoints.sort(function (a, b) { return a - b; });

            // Create length to the last breakpoint by adding the length of the first breakpoint
            let lastValue = gaugeOptions.breakpoints[gaugeOptions.breakpoints.length - 1];
            let average = lastValue / gaugeOptions.breakpoints.length;
            gaugeOptions.maxValue = lastValue + average;

            // Transform objective colors array into an array of hex color segments
            gaugeOptions.colorSegments = this.gaugeData.Metric_Data.objectiveData.poColors.map(function (poc) { return poc.HexColor; });
            if (gaugeOptions.direction === 0) // Reverse colors on down metrics
                gaugeOptions.colorSegments.reverse();
        }
        else {
            // Fake gauge
            gaugeOptions.hover = false;
            gaugeOptions.hoverInvert = false;
            gaugeOptions.breakpoints = [];
            gaugeOptions.colorSegments = ['#a8afb9']; // gray5
            gaugeOptions.minValue = 0;
            gaugeOptions.maxValue = gaugeOptions.value + 1;
        }

        if (!this.gaugeObject)
            this.gaugeObject = new Gauge(this.gaugeContainer?.nativeElement, gaugeOptions);
        else
            this.gaugeObject.opts = gaugeOptions;
    }

    getStyles() {
        return { 
            'visibility': (this.gaugeState !== undefined && this.gaugeState.GaugeTitle === '') ? 'hidden' : 'visible', 
            'font-size': this.titleFontSize,
            'white-space': 'nowrap',
            'overflow': 'hidden',
            'text-overflow': 'ellipsis', 
        };
    }
    
}

